import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert relative `/api/uploads/file/...` to absolute. Leave external URLs untouched.
export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};

export const ImageUploader = ({
  label = "Görseller",
  value = [], // array of URLs
  onChange,
  multiple = true,
  primaryUrl = "",
  onSetPrimary,
  testIdPrefix = "image-uploader",
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: yalnızca görsel`);
          continue;
        }
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`${file.name}: 15 MB'ı aşıyor`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await api.post("/uploads/image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push(data.url);
        } catch (e) {
          const detail = e?.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : `${file.name}: yükleme başarısız`);
        }
      }
      if (uploaded.length) {
        onChange(multiple ? [...value, ...uploaded] : uploaded.slice(-1));
        toast.success(`${uploaded.length} görsel yüklendi`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs tracking-[0.18em] uppercase text-[var(--navy)]">{label}</label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-add`}
          className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[var(--gold)] hover:text-[var(--navy)] transition-colors"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Yükleniyor…" : "Fotoğraf Ekle"}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        data-testid={`${testIdPrefix}-input`}
      />

      {value.length === 0 ? (
        <div
          className="border border-dashed border-[var(--navy)]/30 p-6 text-center text-sm text-[var(--slate)] cursor-pointer hover:border-[var(--gold)] transition-colors"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-empty`}
        >
          <ImageIcon size={22} className="mx-auto mb-2 text-[var(--gold)]" />
          Fotoğraf eklemek için tıklayın veya buraya sürükleyin
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testIdPrefix}-grid`}>
          {value.map((url, i) => {
            const isPrimary = onSetPrimary && url === primaryUrl;
            return (
              <div key={url + i} className="relative group border border-[var(--gold)]/25" data-testid={`${testIdPrefix}-item-${i}`}>
                <img src={resolveImageUrl(url)} alt={`upload-${i}`} className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-[var(--navy)]/0 group-hover:bg-[var(--navy)]/40 transition-colors flex items-start justify-end gap-1 p-1.5">
                  {onSetPrimary && (
                    <button
                      type="button"
                      onClick={() => onSetPrimary(url)}
                      title={isPrimary ? "Ana görsel (mevcut)" : "Ana görsel yap"}
                      className={`p-1.5 rounded-sm transition-all ${isPrimary ? "bg-[var(--gold)] text-[var(--navy)]" : "bg-[var(--ivory)]/90 text-[var(--navy)] opacity-0 group-hover:opacity-100 hover:bg-[var(--gold)]"}`}
                      data-testid={`${testIdPrefix}-primary-${i}`}
                    >
                      <Star size={13} fill={isPrimary ? "currentColor" : "none"} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(i)}
                    title="Kaldır"
                    className="p-1.5 rounded-sm bg-[var(--ivory)]/90 text-red-600 opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-[var(--ivory)] transition-all"
                    data-testid={`${testIdPrefix}-remove-${i}`}
                  >
                    <X size={13} />
                  </button>
                </div>
                {isPrimary && (
                  <span className="absolute bottom-1 left-1 text-[0.6rem] tracking-[0.2em] uppercase bg-[var(--gold)] text-[var(--navy)] px-1.5 py-0.5">
                    Ana
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
